/**
 * Viewport context menu for still-RMB clicks.
 *
 * Routing and item lists are pure so tests can cover kind selection and
 * action-name parity without a browser. The DOM helper only presents those
 * items and invokes the same `main.js` action table the menubar uses.
 */

export const CONTEXT_MENU_KINDS = ['object', 'face', 'edge', 'empty'];

const OBJECT_ITEMS = [
  { action: 'edit-mesh', label: 'Edit Mesh', hint: 'Tab' },
  { action: 'duplicate', label: 'Duplicate', hint: 'Ctrl+D' },
  { action: 'mirror', label: 'Mirror options', hint: 'M' },
  { action: 'group', label: 'Group', hint: 'Ctrl+G' },
  { action: 'ungroup', label: 'Ungroup', hint: 'Ctrl+Shift+G' },
  { action: 'delete', label: 'Delete', hint: 'Delete' },
  { separator: true },
  { action: 'frame-selection', label: 'Frame Selected', hint: 'F' },
];

const FACE_ITEMS = [
  { action: 'extrude-face', label: 'Extrude Face', hint: 'X' },
  { action: 'inset-face', label: 'Inset Face', hint: 'I' },
  { action: 'scale-face', label: 'Face Scale', hint: 'S' },
  { action: 'subdivide-face', label: 'Subdivide Face' },
  { action: 'frame-selection', label: 'Frame Selected', hint: 'F' },
];

const EDGE_ITEMS = [
  { action: 'bevel-edge', label: 'Bevel Edge' },
  { action: 'loop-cut', label: 'Loop Cut' },
  { action: 'frame-selection', label: 'Frame Selected', hint: 'F' },
];

const EMPTY_ITEMS = [
  { action: 'frame-all', label: 'Frame All', hint: 'Home' },
  { action: 'add-box', label: 'Add Box' },
  { action: 'add-sphere', label: 'Add Sphere' },
  { action: 'add-cylinder', label: 'Add Cylinder' },
  { separator: true },
  { action: 'frame-selection', label: 'Frame Selection', hint: 'F' },
];

/** Which menu a still-RMB click should open, given existing pick results. */
export function contextMenuKind({ meshEditActive, elementType, hitFaceId, hitEdgeId, hitObjectId } = {}) {
  if (meshEditActive && elementType === 'face' && hitFaceId) return 'face';
  if (meshEditActive && elementType === 'edge' && hitEdgeId) return 'edge';
  if (!meshEditActive && hitObjectId) return 'object';
  return 'empty';
}

export function contextMenuItems(kind) {
  if (kind === 'object') return OBJECT_ITEMS;
  if (kind === 'face') return FACE_ITEMS;
  if (kind === 'edge') return EDGE_ITEMS;
  return EMPTY_ITEMS;
}

/** True when the pick should be selected through the existing selection API first. */
export function hitNeedsSelect(selectedIds, hitId) {
  return Boolean(hitId) && !(selectedIds ?? []).includes(hitId);
}

export function createContextMenu({
  actions = {},
  getDisabled = () => ({}),
  document: doc = globalThis.document,
} = {}) {
  if (!doc) {
    return {
      get isOpen() { return false; },
      get open() { return false; },
      get kind() { return null; },
      open() {},
      close() {},
      refresh() {},
      destroy() {},
    };
  }

  const root = doc.createElement('div');
  root.className = 'context-menu';
  root.hidden = true;
  root.setAttribute('role', 'menu');
  root.setAttribute('aria-hidden', 'true');
  doc.body.append(root);

  let kind = null;
  let open = false;

  function setOpen(next, nextKind = kind) {
    open = next;
    kind = next ? nextKind : null;
    root.hidden = !next;
    root.dataset.open = String(next);
    root.setAttribute('aria-hidden', String(!next));
    if (!next) root.replaceChildren();
  }

  function applyDisabled() {
    const disabled = getDisabled() ?? {};
    for (const item of root.querySelectorAll('.menu-item')) {
      const isDisabled = Boolean(disabled[item.dataset.action]);
      item.disabled = isDisabled;
      item.setAttribute('aria-disabled', String(isDisabled));
    }
  }

  function positionAt(x, y) {
    root.style.left = `${x}px`;
    root.style.top = `${y}px`;
    const width = root.offsetWidth || 198;
    const height = root.offsetHeight || 0;
    const maxX = Math.max(0, (doc.documentElement?.clientWidth ?? x + width) - width - 8);
    const maxY = Math.max(0, (doc.documentElement?.clientHeight ?? y + height) - height - 8);
    root.style.left = `${Math.min(Math.max(8, x), maxX)}px`;
    root.style.top = `${Math.min(Math.max(8, y), maxY)}px`;
  }

  async function runAction(name) {
    const action = actions[name];
    if (!action) return;
    try {
      await action();
    } catch (err) {
      console.error(`[anvil] context menu action '${name}' failed`, err);
    }
  }

  function onRootClick(event) {
    event.stopPropagation();
    if (root.dataset.opening === 'true') {
      event.preventDefault();
      return;
    }
    const item = event.target.closest('.menu-item');
    if (!item || item.disabled) return;
    const name = item.dataset.action;
    setOpen(false);
    void runAction(name);
  }

  function onDocumentPointerDown(event) {
    if (!open) return;
    if (root.contains(event.target)) return;
    setOpen(false);
  }

  function onKeyDown(event) {
    if (!open || event.key !== 'Escape') return;
    event.preventDefault();
    setOpen(false);
  }

  root.addEventListener('click', onRootClick);
  root.addEventListener('contextmenu', (event) => event.preventDefault());
  doc.addEventListener('pointerdown', onDocumentPointerDown);
  doc.addEventListener('keydown', onKeyDown);

  return {
    // `open()` below shadows a same-named getter, so callers that need the
    // boolean must use `isOpen`.
    get isOpen() {
      return open;
    },
    get kind() {
      return kind;
    },
    get element() {
      return root;
    },
    open({ kind: nextKind, x, y }) {
      const items = contextMenuItems(nextKind);
      root.replaceChildren();
      for (const item of items) {
        if (item.separator) {
          const separator = doc.createElement('div');
          separator.className = 'menu-separator';
          separator.setAttribute('role', 'separator');
          root.append(separator);
          continue;
        }
        const button = doc.createElement('button');
        button.type = 'button';
        button.className = 'menu-item';
        button.dataset.action = item.action;
        button.setAttribute('role', 'menuitem');
        button.append(doc.createTextNode(item.label));
        if (item.hint) {
          const hint = doc.createElement('span');
          hint.className = 'hint';
          hint.textContent = item.hint;
          button.append(hint);
        }
        root.append(button);
      }
      setOpen(true, nextKind);
      applyDisabled();
      positionAt(x, y);
      // Ignore a same-gesture click that lands on the newly opened menu
      // (pointerup opened it under the cursor).
      root.dataset.opening = 'true';
      queueMicrotask(() => { delete root.dataset.opening; });
    },
    close() {
      if (open) setOpen(false);
    },
    refresh() {
      if (open) applyDisabled();
    },
    destroy() {
      setOpen(false);
      root.removeEventListener('click', onRootClick);
      doc.removeEventListener('pointerdown', onDocumentPointerDown);
      doc.removeEventListener('keydown', onKeyDown);
      root.remove();
    },
  };
}
